import {
    X,
    Minus,
    Plus,
    RefreshCw,
    ChevronDown,
} from "lucide-react";
import React, { useEffect, useState } from "react";

interface ItemsCompProps {
    productImage?: string;
    productTitle: string;
    brandName?: string;
    variantDetails?: string;
    quantity?: number;
    price: string;
    originalPrice?: string;
    discount?: string;
    imageHeight?: string;
    priceText?: string;
    divClass?: string;
    onRemove?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    isReward?: boolean;
    deliveryDate?: string;

    // Subscription
    isSubscription?: boolean;
    billingCycle?: string | number;
    interval?: number;

    onSubscriptionChange?: (
        billingCycle: number,
        interval: number
    ) => void;

    isSubscriptionUpdating?: boolean;

    // Pet
    isPet?: boolean;
    purchaseType?: number | string;
    remainingAmount?: number;
    totalAmount?: number;

    onPetPurchaseTypeChange?: (
        purchaseType: number
    ) => void;

    isPetPurchaseTypeUpdating?: boolean;
}

const ItemsComp: React.FC<ItemsCompProps> = ({
    productImage,
    productTitle,
    brandName,
    variantDetails,
    quantity,
    discount,
    originalPrice,
    price,
    imageHeight,
    priceText,
    divClass,
    onRemove,
    onIncrement,
    onDecrement,
    isReward,
    deliveryDate,

    // Subscription
    isSubscription = false,
    billingCycle,
    interval = 1,
    onSubscriptionChange,
    isSubscriptionUpdating = false,

    // Pet
    isPet = false,
    purchaseType,
    remainingAmount = 0,
    totalAmount = 0,
    onPetPurchaseTypeChange,
    isPetPurchaseTypeUpdating = false,
}) => {
    /* ============================================================
       DROPDOWN STATES
    ============================================================ */

    const [subscriptionOpen, setSubscriptionOpen] =
        useState(false);

    const [customOpen, setCustomOpen] =
        useState(false);

    /* ============================================================
       NORMALIZE BILLING CYCLE
    ============================================================ */

    const normalizeBillingCycle = (
        value?: string | number
    ): number => {
        if (
            value === 1 ||
            String(value).toLowerCase() === "weekly"
        ) {
            return 1;
        }

        if (
            value === 3 ||
            String(value).toLowerCase() === "custom"
        ) {
            return 3;
        }

        return 2;
    };

    /* ============================================================
       SUBSCRIPTION STATE
    ============================================================ */

    const [selectedBillingCycle, setSelectedBillingCycle] =
        useState<number>(
            normalizeBillingCycle(billingCycle)
        );

    const [selectedInterval, setSelectedInterval] =
        useState<number>(
            interval || 1
        );

    /* ============================================================
       SYNC SUBSCRIPTION
    ============================================================ */

    useEffect(() => {
        setSelectedBillingCycle(
            normalizeBillingCycle(billingCycle)
        );

        setSelectedInterval(
            interval || 1
        );
    }, [billingCycle, interval]);

    /* ============================================================
       SUBSCRIPTION LABEL
    ============================================================ */

    const getSubscriptionLabel = () => {
        if (!isSubscription) {
            return null;
        }

        if (selectedBillingCycle === 1) {
            return "Autoship: Weekly";
        }

        if (selectedBillingCycle === 2) {
            return "Autoship: Monthly";
        }

        return selectedInterval === 1
            ? "Autoship: Month"
            : `Autoship: Every ${selectedInterval} Months`;
    };

    const subLabel = getSubscriptionLabel();

    /* ============================================================
       SUBSCRIPTION CHANGE
    ============================================================ */

    const handleSubscriptionChange = (
        cycle: number,
        newInterval: number
    ) => {
        setSelectedBillingCycle(cycle);
        setSelectedInterval(newInterval);

        setSubscriptionOpen(false);
        setCustomOpen(false);

        onSubscriptionChange?.(
            cycle,
            newInterval
        );
    };

    /* ============================================================
       FULL PAYMENT CHECK
    ============================================================ */

    const isFullPayment =
        purchaseType === 2 ||
        String(purchaseType).toLowerCase() === "2" ||
        String(purchaseType).toLowerCase() === "fullpayment";

    return (
        <div
            className={`
                border
                border-slate-100
                rounded-2xl

                p-3
                sm:p-4

                bg-white
                relative

                flex
                flex-row

                sm:items-center
                sm:justify-between

                gap-2
                sm:gap-5

                transition
                text-left
                overflow-visible

                ${divClass || ""}
            `}
        >
            {/* =====================================================
                REMOVE BUTTON
            ===================================================== */}

            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="
                        absolute

                        top-2
                        right-2

                        sm:top-3
                        sm:right-3

                        p-1.5

                        text-slate-400
                        hover:text-rose-500
                        hover:bg-slate-50

                        rounded-lg

                        transition
                        cursor-pointer

                        z-30
                    "
                >
                    <X
                        size={16}
                        className="stroke-[2.5]"
                    />
                </button>
            )}

            {/* =====================================================
                LEFT SIDE
                IMAGE + PRODUCT INFORMATION
            ===================================================== */}

            <div
                className="
                    flex
                    items-start
                    sm:items-center

                    gap-3
                    sm:gap-4

                    flex-1
                    min-w-0

                    pr-7
                    sm:pr-0
                "
            >
                {/* =================================================
                    PRODUCT IMAGE
                ================================================= */}

                <div
                    className={`
                        ${
                            imageHeight ||
                            "w-16 h-16 sm:w-20 sm:h-20"
                        }

                        bg-slate-50

                        rounded-xl

                        overflow-hidden

                        shrink-0

                        border
                        border-slate-100

                        flex
                        items-center
                        justify-center
                    `}
                >
                    {productImage ? (
                        <img
                            src={productImage}
                            alt=""
                            className="
                                w-full
                                h-full
                                object-cover
                            "
                        />
                    ) : (
                        <div
                            className="
                                w-6
                                h-6

                                rounded-full

                                bg-slate-200

                                animate-pulse
                            "
                        />
                    )}
                </div>

                {/* =================================================
                    PRODUCT INFORMATION
                ================================================= */}

                <div
                    className="
                        min-w-0
                        flex-1

                        space-y-1

                        overflow-visible
                    "
                >
                    {/* =================================================
                        BRAND
                    ================================================= */}

                    {brandName && (
                        <p
                            className="
                                text-[10px]
                                sm:text-xs

                                font-black
                                uppercase
                                tracking-wider

                                text-[#2F5E2E]

                                truncate
                            "
                        >
                            {brandName}
                        </p>
                    )}

                    {/* =================================================
                        PRODUCT TITLE
                    ================================================= */}

                    <h4
                        className="
                            text-sm
                            sm:text-sm

                            font-bold
                            text-slate-800

                            line-clamp-2

                            leading-snug

                            break-words

                            pr-1
                        "
                    >
                        {productTitle}
                    </h4>

                    {/* =================================================
                        VARIANT
                    ================================================= */}

                    {variantDetails && (
                        <p
                            className="
                                text-[11px]
                                sm:text-xs

                                text-slate-400

                                font-semibold

                                truncate

                                max-w-full

                                sm:max-w-[220px]
                            "
                        >
                            {variantDetails}
                        </p>
                    )}

                    {/* =================================================
                        SUBSCRIPTION
                    ================================================= */}

                    {subLabel && (
                        <div
                            className="
                                relative
                                inline-block

                                mt-1

                                z-30

                                max-w-full
                            "
                        >
                            <button
                                type="button"
                                disabled={
                                    isSubscriptionUpdating
                                }
                                onClick={() => {
                                    setSubscriptionOpen(
                                        (prev) => !prev
                                    );

                                    setCustomOpen(false);
                                }}
                                className="
                                    flex
                                    items-center

                                    gap-1.5

                                    text-[11px]
                                    sm:text-xs

                                    text-emerald-700

                                    bg-emerald-50

                                    font-black

                                    border
                                    border-emerald-100

                                    px-2.5

                                    py-1

                                    rounded-md

                                    w-fit
                                    max-w-full

                                    select-none
                                    cursor-pointer
                                "
                            >
                                <RefreshCw
                                    size={12}
                                    className="
                                        animate-spin-slow
                                        text-emerald-600
                                        stroke-[3]
                                        shrink-0
                                    "
                                />

                                <span className="truncate">
                                    {subLabel}
                                </span>

                                <ChevronDown
                                    size={12}
                                    className={`
                                        transition-transform
                                        shrink-0

                                        ${
                                            subscriptionOpen
                                                ? "rotate-180"
                                                : ""
                                        }
                                    `}
                                />
                            </button>

                            {/* SUBSCRIPTION DROPDOWN */}

                            {subscriptionOpen && (
                                <div
                                    className="
                                        absolute

                                        left-0
                                        top-full

                                        mt-1.5

                                        w-48

                                        max-w-[calc(100vw-40px)]

                                        bg-white

                                        border
                                        border-slate-200

                                        rounded-xl

                                        shadow-xl

                                        overflow-hidden

                                        z-50
                                    "
                                >
                                    {/* WEEKLY */}

                                    <button
                                        type="button"
                                        disabled={
                                            isSubscriptionUpdating
                                        }
                                        onClick={() =>
                                            handleSubscriptionChange(
                                                1,
                                                1
                                            )
                                        }
                                        className={`
                                            w-full

                                            text-left

                                            px-3.5
                                            py-2.5

                                            text-xs

                                            font-bold

                                            transition

                                            ${
                                                selectedBillingCycle === 1
                                                    ? "bg-emerald-50 text-emerald-700"
                                                    : "text-slate-600 hover:bg-slate-50"
                                            }
                                        `}
                                    >
                                        Weekly
                                    </button>

                                    {/* MONTHLY */}

                                    <button
                                        type="button"
                                        disabled={
                                            isSubscriptionUpdating
                                        }
                                        onClick={() =>
                                            handleSubscriptionChange(
                                                2,
                                                1
                                            )
                                        }
                                        className={`
                                            w-full

                                            text-left

                                            px-3.5
                                            py-2.5

                                            text-xs

                                            font-bold

                                            transition

                                            ${
                                                selectedBillingCycle === 2
                                                    ? "bg-emerald-50 text-emerald-700"
                                                    : "text-slate-600 hover:bg-slate-50"
                                            }
                                        `}
                                    >
                                        Monthly
                                    </button>

                                    {/* CUSTOM */}

                                    <div
                                        className="
                                            border-t
                                            border-slate-100
                                        "
                                    >
                                        <button
                                            type="button"
                                            disabled={
                                                isSubscriptionUpdating
                                            }
                                            onClick={() =>
                                                setCustomOpen(
                                                    (prev) =>
                                                        !prev
                                                )
                                            }
                                            className="
                                                w-full

                                                flex
                                                items-center
                                                justify-between

                                                text-left

                                                px-3.5
                                                py-2.5

                                                text-xs

                                                font-bold

                                                text-slate-600

                                                hover:bg-slate-50

                                                transition
                                            "
                                        >
                                            <span>
                                                Custom Interval
                                            </span>

                                            <ChevronDown
                                                size={12}
                                                className={`
                                                    transition-transform

                                                    ${
                                                        customOpen
                                                            ? "rotate-180"
                                                            : ""
                                                    }
                                                `}
                                            />
                                        </button>

                                        {customOpen && (
                                            <div
                                                className="
                                                    bg-slate-50

                                                    border-t
                                                    border-slate-100

                                                    py-1
                                                "
                                            >
                                                {[2, 3, 4, 6].map(
                                                    (months) => (
                                                        <button
                                                            key={months}
                                                            type="button"
                                                            disabled={
                                                                isSubscriptionUpdating
                                                            }
                                                            onClick={() =>
                                                                handleSubscriptionChange(
                                                                    3,
                                                                    months
                                                                )
                                                            }
                                                            className={`
                                                                w-full

                                                                text-left

                                                                px-5
                                                                py-2

                                                                text-xs

                                                                font-semibold

                                                                transition

                                                                ${
                                                                    selectedBillingCycle === 3 &&
                                                                    selectedInterval === months
                                                                        ? "text-emerald-700 bg-emerald-100 font-bold"
                                                                        : "text-slate-500 hover:bg-slate-100"
                                                                }
                                                            `}
                                                        >
                                                            Every{" "}
                                                            {months}{" "}
                                                            Months
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* =================================================
                        QUANTITY
                    ================================================= */}

                    {quantity !== undefined && (
                        <div className="pt-2">
                            {onIncrement &&
                            onDecrement ? (
                                <div
                                    className="
                                        flex
                                        items-center

                                        border
                                        border-slate-200

                                        rounded-lg

                                        w-fit

                                        bg-slate-50/50

                                        overflow-hidden

                                        shadow-3xs
                                    "
                                >
                                    <button
                                        type="button"
                                        onClick={
                                            onDecrement
                                        }
                                        className="
                                            p-2
                                            px-2.5

                                            text-slate-500

                                            hover:text-rose-600
                                            hover:bg-slate-100

                                            transition

                                            active:scale-95
                                        "
                                    >
                                        <Minus
                                            size={13}
                                            className="stroke-[3]"
                                        />
                                    </button>

                                    <span
                                        className="
                                            px-3

                                            text-sm

                                            font-black

                                            text-slate-800

                                            min-w-[28px]

                                            text-center

                                            select-none
                                        "
                                    >
                                        {quantity}
                                    </span>

                                    <button
                                        type="button"
                                        onClick={
                                            onIncrement
                                        }
                                        className="
                                            p-2
                                            px-2.5

                                            text-slate-500

                                            hover:text-[#2F5E2E]
                                            hover:bg-slate-100

                                            transition

                                            active:scale-95
                                        "
                                    >
                                        <Plus
                                            size={13}
                                            className="stroke-[3]"
                                        />
                                    </button>
                                </div>
                            ) : (
                                <p
                                    className="
                                        text-xs

                                        text-slate-500

                                        font-medium
                                    "
                                >
                                    Qty:{" "}
                                    <span
                                        className="
                                            font-bold
                                            text-slate-800
                                        "
                                    >
                                        {quantity}
                                    </span>
                                </p>
                            )}
                        </div>
                    )}

                    {/* =================================================
                        PET PURCHASE TYPE
                    ================================================= */}

                    {isPet && (
                        <div
                            className="
                                mt-2

                                flex
                                flex-wrap
                                items-center

                                gap-2
                            "
                        >
                            <span
                                className="
                                    text-[11px]
                                    sm:text-xs

                                    font-bold

                                    text-slate-700

                                    bg-slate-100

                                    px-2.5
                                    py-1

                                    rounded-md

                                    whitespace-nowrap
                                "
                            >
                                {isFullPayment
                                    ? "Full Payment"
                                    : "Partial Booking"}
                            </span>

                            {onPetPurchaseTypeChange && (
                                <button
                                    type="button"
                                    disabled={
                                        isPetPurchaseTypeUpdating
                                    }
                                    onClick={() =>
                                        onPetPurchaseTypeChange(
                                            isFullPayment
                                                ? 1
                                                : 2
                                        )
                                    }
                                    className="
                                        text-[11px]
                                        sm:text-xs

                                        font-bold

                                        text-emerald-700

                                        bg-emerald-50

                                        hover:bg-emerald-100

                                        border
                                        border-emerald-200

                                        px-2.5
                                        py-1

                                        rounded-md

                                        transition

                                        cursor-pointer

                                        whitespace-nowrap
                                    "
                                >
                                    {isPetPurchaseTypeUpdating
                                        ? "Updating..."
                                        : isFullPayment
                                            ? "Switch to Partial Booking"
                                            : "Make Full Payment"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* =================================================
                        DELIVERY DATE
                    ================================================= */}

                    {deliveryDate && (
                        <p
                            className="
                                text-[11px]
                                sm:text-xs

                                text-emerald-600

                                font-bold

                                bg-emerald-50/50

                                border
                                border-emerald-100/30

                                px-2.5
                                py-1

                                rounded-md

                                w-fit
                                max-w-full

                                mt-1

                                truncate
                            "
                        >
                            {deliveryDate}
                        </p>
                    )}
                </div>
            </div>

            {/* =====================================================
                PRICE SECTION

                MOBILE:
                - stays on right
                - starts below the X
                - does not overlap remove button

                DESKTOP:
                - normal right-side price
            ===================================================== */}

            <div
                className="
                    w-auto
                    sm:w-auto

                    min-w-[85px]
                    sm:min-w-[150px]

                    shrink-0

                    self-start
                    sm:self-center

                    pt-4
                    sm:pt-0

                    pr-1
                    sm:pr-0

                    text-right
                "
            >
                {/* =================================================
                    TOTAL
                ================================================= */}

                {isPet &&
                    totalAmount > 0 && (
                        <p
                            className="
                                text-[11px]

                                text-slate-600

                                font-semibold

                                leading-5

                                whitespace-nowrap
                            "
                        >
                            Total:{" "}
                            <span className="font-bold text-slate-800">
                                AED{" "}
                                {Number(
                                    totalAmount
                                ).toFixed(2)}
                            </span>
                        </p>
                    )}

                {/* =================================================
                    REMAINING
                ================================================= */}

                {isPet &&
                    remainingAmount > 0 &&
                    !isFullPayment && (
                        <p
                            className="
                                text-[11px]

                                text-amber-600

                                font-semibold

                                leading-5

                                whitespace-nowrap
                            "
                        >
                            Remaining:{" "}
                            <span className="font-bold">
                                AED{" "}
                                {Number(
                                    remainingAmount
                                ).toFixed(2)}
                            </span>
                        </p>
                    )}

                {/* =================================================
                    CURRENT PRICE
                ================================================= */}

                <p
                    className={`
                        font-black

                        text-slate-800

                        mt-1

                        leading-5

                        break-words

                        ${
                            priceText ||
                            "text-sm"
                        }
                    `}
                >
                    {price}
                </p>

                {/* =================================================
                    ORIGINAL PRICE
                ================================================= */}

                {originalPrice && (
                    <p
                        className="
                            text-xs

                            line-through

                            text-slate-400

                            font-medium

                            whitespace-nowrap
                        "
                    >
                        {originalPrice}
                    </p>
                )}

                {/* =================================================
                    DISCOUNT
                ================================================= */}

                {discount && (
                    <p
                        className="
                            text-xs

                            text-rose-500

                            font-black

                            tracking-tight

                            whitespace-nowrap
                        "
                    >
                        {discount}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ItemsComp;